"use client";

import { motion, useAnimation, useReducedMotion, Variants } from "framer-motion";
import { forwardRef, useCallback, useImperativeHandle, useRef } from "react";

export interface UserPlusIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

interface UserPlusIconProps {
  size?: number;
  duration?: number;
  isAnimated?: boolean;
  isHovered?: boolean;
  className?: string;
}

const UserPlusIcon = forwardRef<UserPlusIconHandle, UserPlusIconProps>(
  (
    {
      className,
      size = 24,
      duration = 1,
      isAnimated = true,
      isHovered = false,
    },
    ref,
  ) => {
    const controls = useAnimation();
    const reduced = useReducedMotion();
    const isControlled = useRef(false);
    const prevHovered = useRef(isHovered);

    useImperativeHandle(ref, () => {
      isControlled.current = true;
      return {
        startAnimation: () =>
          reduced ? controls.start("normal") : controls.start("animate"),
        stopAnimation: () => controls.start("normal"),
      };
    });

    // React to isHovered prop changes
    if (isHovered !== prevHovered.current) {
      prevHovered.current = isHovered;
      if (isHovered && isAnimated && !reduced) {
        controls.start("animate");
      } else if (!isHovered) {
        controls.start("normal");
      }
    }

    const handleEnter = useCallback(() => {
      if (!isAnimated || reduced) return;
      if (!isControlled.current) controls.start("animate");
    }, [controls, reduced, isAnimated]);

    const handleLeave = useCallback(() => {
      if (!isControlled.current) controls.start("normal");
    }, [controls]);

    const bodyVariants: Variants = {
      normal: { strokeDashoffset: 0, opacity: 1 },
      animate: {
        strokeDashoffset: [40, 0],
        opacity: [0.3, 1],
        transition: { duration: 0.6 * duration, ease: "easeInOut" },
      },
    };

    const headVariants: Variants = {
      normal: { scale: 1, opacity: 1 },
      animate: {
        scale: [0.5, 1.2, 1],
        opacity: [0, 1],
        transition: { duration: 0.6 * duration, ease: "easeInOut" },
      },
    };

    const plusVariants: Variants = {
      normal: { scale: 1, rotate: 0, opacity: 1 },
      animate: {
        scale: [1, 1.3, 1],
        rotate: [0, 25, -25, 0],
        opacity: 1,
        transition: { duration: 0.6 * duration, ease: "easeInOut" },
      },
    };

    return (
      <motion.svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        style={{ flexShrink: 0 }}
      >
        <motion.path
          d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"
          strokeDasharray="40"
          strokeDashoffset="40"
          variants={bodyVariants}
          initial="normal"
          animate={controls}
        />
        <motion.circle
          cx="9"
          cy="7"
          r="4"
          variants={headVariants}
          initial="normal"
          animate={controls}
        />
        <motion.g variants={plusVariants} initial="normal" animate={controls}>
          <motion.line x1="19" x2="19" y1="8" y2="14" />
          <motion.line x1="22" x2="16" y1="11" y2="11" />
        </motion.g>
      </motion.svg>
    );
  },
);

UserPlusIcon.displayName = "UserPlusIcon";
export { UserPlusIcon };
